<template>
    <div class="card mb-3 p-3 calendar-picker-v2" @click.stop>
        <div class="row">

            <div class="col-md-3 mb-1">
                <label>Docente</label>
                <select
                    class="form-control select-control"
                    v-model="localValue.docente_id"
                >
                    <option :value="null">
                        {{ localValue.modulo_id ? '-- Docenti compatibili col modulo --' : '-- Seleziona docente --' }}
                    </option>
                    <option v-for="d in filteredDocenti" :key="d.id" :value="d.id">
                        {{ d.full_name || d.nome }}
                    </option>
                </select>
                <small class="text-muted" v-if="localValue.modulo_id && !filteredDocenti.length">
                    Nessun docente associato al modulo selezionato.
                </small>
            </div>

            <div class="col-md-3 mb-1">
                <label>Modulo</label>
                <select
                    class="form-control select-control"
                    v-model="localValue.modulo_id"
                >
                    <option :value="null">
                        {{ localValue.docente_id ? '-- Moduli compatibili col docente --' : '-- Seleziona modulo --' }}
                    </option>
                    <option
                        v-for="m in filteredModuli"
                        :key="m.id"
                        :value="m.id"
                        :disabled="isModuloCompleted(m)"
                    >
                        {{ moduloOptionLabel(m) }}
                    </option>
                </select>
                <small class="text-muted" v-if="localValue.docente_id && !filteredModuli.length">
                    Nessun modulo associato al docente selezionato.
                </small>
            </div>

            <div class="col-md-3 mb-1 tutor-autocomplete">
                <label>Tutor</label>
                <input
                    type="text"
                    class="form-control"
                    v-model="tutorSearch"
                    placeholder="Cerca tutor..."
                    @input="onTutorSearchInput"
                    @focus="showTutorResults = true"
                >

                <div v-if="selectedTutorLabel" class="selected-tutor mt-1">
                    <span>{{ selectedTutorLabel }}</span>
                    <button type="button" class="btn btn-xs btn-link p-0 ml-1" @click="clearTutor">
                        rimuovi
                    </button>
                </div>

                <div
                    v-if="showTutorResults && filteredTutors.length"
                    class="tutor-results"
                >
                    <div
                        v-for="t in filteredTutors"
                        :key="t.id"
                        class="tutor-result-item"
                        @click="selectTutor(t)"
                    >
                        {{ t.full_name || t.name || t.nome }}
                    </div>
                </div>

                <small class="text-muted" v-if="localValue.tutor_id">
                    Il tutor verrà pianificato per l’intera durata dello slot cliccato.
                </small>
            </div>

            <div class="col-md-3 mb-1 codocente-autocomplete">
                <label>Co-docente</label>
                <input
                    type="text"
                    class="form-control"
                    v-model="codocenteSearch"
                    placeholder="Cerca co-docente..."
                    @input="onCodocenteSearchInput"
                    @focus="showCodocenteResults = true"
                >

                <div v-if="selectedCodocenteLabel" class="selected-codocente mt-1">
                    <span>{{ selectedCodocenteLabel }}</span>
                    <button type="button" class="btn btn-xs btn-link p-0 ml-1" @click="clearCodocente">
                        rimuovi
                    </button>
                </div>

                <div
                    v-if="showCodocenteResults && codocenteOptions.length"
                    class="codocente-results"
                >
                    <div
                        v-for="item in codocenteOptions"
                        :key="item.id"
                        class="codocente-result-item"
                        @click="selectCodocente(item)"
                    >
                        {{ item.full_name || item.text || item.nome }}
                    </div>
                </div>

                <small class="text-muted" v-if="codocenteLoading">Ricerca in corso...</small>
            </div>

        </div>

        <div class="row mt-2 align-items-end calendar-picker-bottom-row">
            <div class="col-md-3 mb-1">
                <label>Aula predefinita</label>
                <select class="form-control select-control" v-model="localValue.aula_id">
                    <option :value="null">-- Aula non specificata --</option>
                    <option v-for="a in aule" :key="a.id" :value="a.id">
                        {{ a.nome }}
                    </option>
                </select>
            </div>

            <div class="col-md-3 mb-1">
                <div class="compact-kpi" :class="moduloClass">
                    <span class="kpi-label">Modulo</span>
                    <strong>{{ moduloResidue }}h</strong>
                    <span class="kpi-muted">residue</span>
                    <small v-if="stats && stats.modulo">
                        · {{ getModuloPlannedHours() }}h / {{ getModuloTotalHours() }}h
                    </small>
                </div>
            </div>

            <div class="col-md-3 mb-1">
                <div class="compact-kpi" :class="docenteClass">
                    <span class="kpi-label">Docente</span>
                    <strong>{{ docenteResidue }}h</strong>
                    <span class="kpi-muted">residue</span>
                    <small v-if="stats && stats.docente">
                        · {{ getDocentePlannedHours() }}h / {{ getDocenteAssignedHours() }}h
                    </small>
                </div>
            </div>

            <div class="col-md-3 mb-1 d-flex align-items-end">
                <div class="text-muted small">
                    Seleziona il modulo e almeno un docente oppure un tutor, poi clicca su una cella libera della
                    griglia per aggiungere la pianificazione.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from './calendarApi.js'

export default {
    props: {
        value: {
            type: Object,
            default: function () {
                return {}
            }
        },
        stats: {
            type: Object,
            default: function () {
                return {}
            }
        },
        moduli: {
            type: Array,
            default: function () {
                return []
            }
        },
        docenti: {
            type: Array,
            default: function () {
                return []
            }
        },
        tutors: {
            type: Array,
            default: function () {
                return []
            }
        },
        aule: {
            type: Array,
            default: function () {
                return []
            }
        },
        config: {
            type: Object,
            default: function () {
                return {}
            }
        }
    },

    data: function () {
        return {
            syncingFromParent: false,
            lastEmittedValue: null,
            tutorSearch: '',
            showTutorResults: false,
            selectedTutorLabel: '',
            codocenteSearch: '',
            codocenteOptions: [],
            codocenteLoading: false,
            codocenteTimer: null,
            showCodocenteResults: false,
            selectedCodocenteLabel: '',
            localValue: this.normalizeValue(this.value)
        }
    },

    mounted: function () {
        document.addEventListener('click', this.handleDocumentClick)
    },

    beforeDestroy: function () {
        document.removeEventListener('click', this.handleDocumentClick)
    },

    watch: {
        value: {
            deep: true,
            handler: function (val) {
                var normalized = this.normalizeValue(val)

                if (this.sameValue(normalized, this.localValue)) {
                    return
                }

                this.syncingFromParent = true
                this.localValue = normalized
                this.syncTutorLabelFromValue(normalized.tutor_id)

                this.$nextTick(function () {
                    this.syncingFromParent = false
                }.bind(this))
            }
        },

        'localValue.docente_id': function () {
            if (
                this.localValue.docente_id &&
                this.localValue.modulo_id &&
                !this.docenteIsCompatibleWithModulo(this.localValue.docente_id, this.localValue.modulo_id)
            ) {
                this.localValue.modulo_id = null
            }
        },

        'localValue.modulo_id': function () {
            if (
                this.localValue.docente_id &&
                this.localValue.modulo_id &&
                !this.docenteIsCompatibleWithModulo(this.localValue.docente_id, this.localValue.modulo_id)
            ) {
                this.localValue.docente_id = null
            }
        },

        localValue: {
            deep: true,
            handler: function (val) {
                if (this.syncingFromParent) {
                    return
                }

                var normalized = this.normalizeValue(val)
                normalized.durata_tutor = 0

                if (
                    normalized.docente_id &&
                    normalized.modulo_id &&
                    !this.docenteIsCompatibleWithModulo(normalized.docente_id, normalized.modulo_id)
                ) {
                    return
                }

                if (this.sameValue(normalized, this.lastEmittedValue)) {
                    return
                }

                this.lastEmittedValue = this.cloneValue(normalized)
                this.$emit('input', this.cloneValue(normalized))
            }
        }
    },

    computed: {
        moduloResidue: function () {
            if (this.stats && this.stats.modulo) {
                if (typeof this.stats.modulo.residue !== 'undefined') {
                    return this.stats.modulo.residue
                }

                if (typeof this.stats.modulo.remaining_hours !== 'undefined') {
                    return this.stats.modulo.remaining_hours
                }
            }

            if (this.stats && typeof this.stats.modulo_residue !== 'undefined') {
                return this.stats.modulo_residue
            }

            return 0
        },

        docenteResidue: function () {
            if (this.stats && this.stats.docente) {
                if (typeof this.stats.docente.residue !== 'undefined') {
                    return this.stats.docente.residue
                }

                if (typeof this.stats.docente.remaining_hours !== 'undefined') {
                    return this.stats.docente.remaining_hours
                }
            }

            if (this.stats && typeof this.stats.docente_residue !== 'undefined') {
                return this.stats.docente_residue
            }

            return 0
        },

        moduloClass: function () {
            var v = Number(this.moduloResidue || 0)
            if (v === 0) return 'compact-kpi-danger'
            if (v <= 3) return 'compact-kpi-warning'
            return 'compact-kpi-success'
        },

        docenteClass: function () {
            var v = Number(this.docenteResidue || 0)
            if (v === 0) return 'compact-kpi-danger'
            if (v <= 3) return 'compact-kpi-warning'
            return 'compact-kpi-success'
        },

        filteredDocenti: function () {
            var moduloId = this.localValue && this.localValue.modulo_id
                ? parseInt(this.localValue.modulo_id, 10)
                : null

            if (!moduloId) {
                return this.docenti || []
            }

            return (this.docenti || []).filter(function (docente) {
                var moduloIds = (docente.modulo_ids || []).map(function (id) {
                    return parseInt(id, 10)
                })

                return moduloIds.indexOf(moduloId) !== -1
            })
        },

        filteredModuli: function () {
            var docenteId = this.localValue && this.localValue.docente_id
                ? parseInt(this.localValue.docente_id, 10)
                : null

            if (!docenteId) {
                return this.moduli || []
            }

            var docente = (this.docenti || []).find(function (item) {
                return parseInt(item.id, 10) === docenteId
            })

            if (!docente) {
                return []
            }

            var moduloIds = (docente.modulo_ids || []).map(function (id) {
                return parseInt(id, 10)
            })

            return (this.moduli || []).filter(function (modulo) {
                return moduloIds.indexOf(parseInt(modulo.id, 10)) !== -1
            })
        },

        filteredTutors: function () {
            var search = String(this.tutorSearch || '').toLowerCase()

            if (!search) {
                return this.tutors || []
            }

            return (this.tutors || []).filter(function (tutor) {
                var label = String(tutor.full_name || tutor.name || tutor.nome || '').toLowerCase()
                return label.indexOf(search) !== -1
            })
        }
    },

    methods: {
        moduloRemainingHours: function (modulo) {
            if (!modulo) return 0

            if (typeof modulo.ore_residue !== 'undefined') {
                return Math.max(0, Number(modulo.ore_residue || 0))
            }

            return Math.max(0, Number(modulo.durata || 0) - Number(modulo.ore_pianificate || 0))
        },

        isModuloCompleted: function (modulo) {
            return this.moduloRemainingHours(modulo) <= 0
        },

        moduloOptionLabel: function (modulo) {
            var label = modulo.nome || modulo.modulo || 'Modulo'
            if (modulo.course_label) {
                label = modulo.course_label + ' · ' + label
            }
            var remaining = this.moduloRemainingHours(modulo)
            var suffix = remaining === 1 ? ' residua' : ' residue'

            return label + ' (' + remaining + 'h' + suffix + ')'
        },

        handleDocumentClick: function () {
            this.showTutorResults = false
            this.showCodocenteResults = false
        },

        docenteIsCompatibleWithModulo: function (docenteId, moduloId) {
            docenteId = parseInt(docenteId || 0, 10)
            moduloId = parseInt(moduloId || 0, 10)

            if (!docenteId || !moduloId) {
                return false
            }

            var docente = (this.docenti || []).find(function (item) {
                return parseInt(item.id, 10) === docenteId
            })

            if (!docente) {
                return false
            }

            var moduloIds = docente.modulo_ids || []

            return moduloIds.map(function (id) {
                return parseInt(id, 10)
            }).indexOf(moduloId) !== -1
        },

        onTutorSearchInput: function () {
            var term = String(this.tutorSearch || '').trim()

            this.selectedTutorLabel = ''
            this.localValue.tutor_id = null

            if (term.length < 1) {
                this.showTutorResults = false
                return
            }

            this.showTutorResults = true
        },

        selectTutor: function (tutor) {
            this.localValue.tutor_id = this.normalizeNullableInt(tutor.id)
            this.selectedTutorLabel = tutor.full_name || tutor.name || tutor.nome || ''
            this.tutorSearch = this.selectedTutorLabel
            this.showTutorResults = false
        },

        clearTutor: function () {
            this.localValue.tutor_id = null
            this.selectedTutorLabel = ''
            this.tutorSearch = ''
            this.showTutorResults = false
        },

        syncTutorLabelFromValue: function (tutorId) {
            tutorId = this.normalizeNullableInt(tutorId)

            if (!tutorId) {
                this.selectedTutorLabel = ''
                if (!this.tutorSearch) {
                    this.tutorSearch = ''
                }
                return
            }

            var tutor = (this.tutors || []).find(function (item) {
                return parseInt(item.id, 10) === tutorId
            })

            if (tutor) {
                this.selectedTutorLabel = tutor.full_name || tutor.name || tutor.nome || ''
                this.tutorSearch = this.selectedTutorLabel
            }
        },

        onCodocenteSearchInput: function () {
            var term = String(this.codocenteSearch || '').trim()

            this.selectedCodocenteLabel = ''
            this.localValue.codocente_id = null

            if (this.codocenteTimer) {
                clearTimeout(this.codocenteTimer)
            }

            if (term.length < 2) {
                this.codocenteOptions = []
                return
            }

            this.codocenteTimer = setTimeout(function () {
                this.searchCodocenti(term)
            }.bind(this), 250)
        },

        searchCodocenti: function (term) {
            if (!this.config || !this.config.api || !this.config.api.teacherSuggest) {
                return
            }

            this.codocenteLoading = true

            api.teacherSuggest(this.config, {
                q: term,
                search: term,
                term: term
            })
                .then(function (response) {
                    var results = response && response.results ? response.results : []
                    this.codocenteOptions = results
                    this.showCodocenteResults = true
                }.bind(this))
                .catch(function (error) {
                    console.error('Errore autocomplete co-docente', error)
                    this.codocenteOptions = []
                }.bind(this))
                .finally(function () {
                    this.codocenteLoading = false
                }.bind(this))
        },

        selectCodocente: function (item) {
            this.localValue.codocente_id = this.normalizeNullableInt(item.id)
            this.selectedCodocenteLabel = item.full_name || item.text || item.nome || ''
            this.codocenteSearch = this.selectedCodocenteLabel
            this.codocenteOptions = []
            this.showCodocenteResults = false
        },

        clearCodocente: function () {
            this.localValue.codocente_id = null
            this.selectedCodocenteLabel = ''
            this.codocenteSearch = ''
            this.codocenteOptions = []
            this.showCodocenteResults = false
        },

        normalizeValue: function (value) {
            value = value || {}

            return {
                modulo_id: this.normalizeNullableInt(value.modulo_id),
                docente_id: this.normalizeNullableInt(value.docente_id),
                tutor_id: this.normalizeNullableInt(value.tutor_id),
                codocente_id: this.normalizeNullableInt(value.codocente_id),
                aula_id: this.normalizeNullableInt(value.aula_id),
                durata_tutor: typeof value.durata_tutor !== 'undefined'
                    ? Number(value.durata_tutor || 0)
                    : 0
            }
        },

        normalizeNullableInt: function (value) {
            if (value === null || typeof value === 'undefined' || value === '') {
                return null
            }

            var parsed = parseInt(value, 10)

            return isNaN(parsed) ? null : parsed
        },

        cloneValue: function (value) {
            return {
                modulo_id: value.modulo_id,
                docente_id: value.docente_id,
                tutor_id: value.tutor_id,
                codocente_id: value.codocente_id,
                aula_id: value.aula_id,
                durata_tutor: value.durata_tutor
            }
        },

        sameValue: function (a, b) {
            if (!a || !b) {
                return false
            }

            return String(a.modulo_id) === String(b.modulo_id) &&
                String(a.docente_id) === String(b.docente_id) &&
                String(a.tutor_id) === String(b.tutor_id) &&
                String(a.codocente_id) === String(b.codocente_id) &&
                String(a.aula_id) === String(b.aula_id)
        },

        getModuloPlannedHours: function () {
            if (this.stats && this.stats.modulo) {
                if (typeof this.stats.modulo.pianificate !== 'undefined') {
                    return this.stats.modulo.pianificate
                }

                if (typeof this.stats.modulo.planned_hours !== 'undefined') {
                    return this.stats.modulo.planned_hours
                }
            }

            return 0
        },

        getModuloTotalHours: function () {
            if (this.stats && this.stats.modulo) {
                if (typeof this.stats.modulo.totale !== 'undefined') {
                    return this.stats.modulo.totale
                }

                if (typeof this.stats.modulo.total_hours !== 'undefined') {
                    return this.stats.modulo.total_hours
                }
            }

            return 0
        },

        getDocentePlannedHours: function () {
            if (this.stats && this.stats.docente) {
                if (typeof this.stats.docente.pianificate !== 'undefined') {
                    return this.stats.docente.pianificate
                }

                if (typeof this.stats.docente.planned_hours !== 'undefined') {
                    return this.stats.docente.planned_hours
                }
            }

            return 0
        },

        getDocenteAssignedHours: function () {
            if (this.stats && this.stats.docente) {
                if (typeof this.stats.docente.assegnate !== 'undefined') {
                    return this.stats.docente.assegnate
                }

                if (typeof this.stats.docente.assigned_hours !== 'undefined') {
                    return this.stats.docente.assigned_hours
                }
            }

            return 0
        }
    }
}
</script>

<style scoped>
.calendar-picker-v2 {
    margin-top: 8px;
    overflow: visible !important;
    position: relative;
    z-index: 25;
}

.calendar-picker-v2 label {
    font-weight: 600;
    font-size: 11px;
    margin-bottom: 3px;
}

.calendar-picker-bottom-row > [class*="col-"] {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
}

.kpi-label {
    font-size: 11px;
    text-transform: uppercase;
    opacity: .75;
}

.compact-kpi {
    min-height: 34px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    padding: 7px 10px;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    font-size: 12px;
    line-height: 1.2;
}

.compact-kpi .kpi-label {
    margin-right: 4px;
}

.compact-kpi strong {
    font-size: 14px;
    font-weight: 800;
}

.kpi-muted {
    opacity: .75;
}

.compact-kpi-success {
    background: #f0fdf4;
    border-color: #bbf7d0;
    color: #166534;
}

.compact-kpi-warning {
    background: #fffbeb;
    border-color: #fde68a;
    color: #92400e;
}

.compact-kpi-danger {
    background: #fff1f2;
    border-color: #fecdd3;
    color: #9f1239;
}

@media (max-width: 1199px) {
    .compact-kpi {
        align-items: flex-start;
        flex-direction: column;
        gap: 2px;
    }
}

.tutor-autocomplete {
    position: relative;
    z-index: 90;
    overflow: visible;
}

.tutor-results {
    position: absolute;
    z-index: 9999;
    left: 15px;
    right: 15px;
    max-height: 220px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, .12);
}

.tutor-result-item {
    padding: 7px 9px;
    cursor: pointer;
    font-size: 12px;
}

.tutor-result-item:hover {
    background: #f1f5f9;
}

.selected-tutor {
    font-size: 11px;
    color: #555;
}

.codocente-autocomplete {
    position: relative;
    z-index: 90;
    overflow: visible;
}

.codocente-results {
    position: absolute;
    z-index: 9999;
    left: 15px;
    right: 15px;
    max-height: 220px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, .12);
}

.codocente-result-item {
    padding: 7px 9px;
    cursor: pointer;
    font-size: 12px;
}

.codocente-result-item:hover {
    background: #f1f5f9;
}

.selected-codocente {
    font-size: 11px;
    color: #555;
}
</style>
